// UltraFlex Admin - Plans Management
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGate from '../../components/PermissionGate';
import {
  Plus, Edit3, Trash2, Save, X, Search, Check,
  MapPin, CreditCard, Dumbbell, Users, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_TYPES = [
  { value: 'member', label: 'Member' },
  { value: 'pt_monthly', label: 'PT Monthly' },
  { value: 'pt_adhoc', label: 'PT Ad-hoc' },
];

const AdminPlans = () => {
  const { token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [sitePriceForm, setSitePriceForm] = useState({ site_id: '', price_monthly: '' });
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const emptyPlan = {
    name: '', code: '', price_monthly: '', description: '',
    features: [''], plan_type: 'member', is_public: true,
    is_active: true, access_all_sites: false,
  };

  useEffect(() => { fetchPlans(); fetchSites(); }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/plans`, { headers });
      const data = await res.json();
      setPlans(data.items || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchSites = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sites`, { headers });
      const data = await res.json();
      setSites(data.items || data || []);
    } catch (err) { console.error(err); }
  };

  const savePlan = async (plan) => {
    const isNew = !plan.id;
    const url = isNew ? `${API_URL}/api/plans` : `${API_URL}/api/plans/${plan.id}`;
    const method = isNew ? 'POST' : 'PATCH';
    const body = {
      name: plan.name,
      code: plan.code || plan.name.toUpperCase().replace(/\s+/g, '_').slice(0, 20),
      price_monthly: parseFloat(plan.price_monthly) || 0,
      description: plan.description,
      features: (plan.features || []).filter(f => f.trim()),
      plan_type: plan.plan_type || 'member',
      is_public: plan.is_public,
      is_active: plan.is_active,
      access_all_sites: plan.access_all_sites,
    };
    try {
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (res.ok) {
        setEditing(null);
        setShowCreate(false);
        fetchPlans();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to save plan');
      }
    } catch (err) { alert('Failed to save plan'); }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Deactivate this plan?')) return;
    await fetch(`${API_URL}/api/plans/${planId}`, { method: 'DELETE', headers });
    fetchPlans();
  };

  const saveSitePrice = async (planId) => {
    if (!sitePriceForm.site_id || !sitePriceForm.price_monthly) return;
    await fetch(`${API_URL}/api/plans/${planId}/site-prices`, {
      method: 'POST', headers,
      body: JSON.stringify({ site_id: sitePriceForm.site_id, price_monthly: parseFloat(sitePriceForm.price_monthly) }),
    });
    setSitePriceForm({ site_id: '', price_monthly: '' });
    fetchPlans();
  };

  const removeSitePrice = async (planId, siteId) => {
    await fetch(`${API_URL}/api/plans/${planId}/site-prices/${siteId}`, { method: 'DELETE', headers });
    fetchPlans();
  };

  const typeColor = (t) => {
    if (t === 'pt_monthly') return 'text-blue-400 bg-blue-500/10';
    if (t === 'pt_adhoc') return 'text-purple-400 bg-purple-500/10';
    return 'text-emerald-400 bg-emerald-500/10';
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Membership Plans</h1>
          <p className="text-zinc-400 text-sm mt-1">Manage plans, pricing, and per-site overrides</p>
        </div>
        <PermissionGate perm="settings.plans">
          <button
            onClick={() => { setShowCreate(true); setEditing({ ...emptyPlan }); }}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            data-testid="create-plan-btn"
          >
            <Plus className="w-4 h-4" /> New Plan
          </button>
        </PermissionGate>
      </div>

      {/* Plan Cards */}
      <div className="space-y-4">
        {plans.map(plan => (
          <div key={plan.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`plan-card-${plan.id}`}>
            {/* Plan Header */}
            <div className="p-5 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColor(plan.plan_type)}`}>
                    {PLAN_TYPES.find(t => t.value === plan.plan_type)?.label || 'Member'}
                  </span>
                  {!plan.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-400">Inactive</span>}
                  {!plan.is_public && <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-400">Hidden</span>}
                </div>
                <p className="text-zinc-500 text-sm truncate">{plan.description || 'No description'}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">£{plan.price_monthly?.toFixed(2)}</p>
                <p className="text-zinc-500 text-xs">/month (site-wide)</p>
              </div>
              <div className="flex items-center gap-2">
                <PermissionGate perm="settings.plans">
                  <button onClick={() => setEditing({ ...plan })} className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800" data-testid={`edit-plan-${plan.id}`}>
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => deletePlan(plan.id)} className="p-2 text-zinc-400 hover:text-red-400 rounded-lg hover:bg-zinc-800" data-testid={`delete-plan-${plan.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </PermissionGate>
                <button
                  onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
                  className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800"
                  data-testid={`expand-plan-${plan.id}`}
                >
                  {expandedPlan === plan.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Expanded: Features & Site Prices */}
            {expandedPlan === plan.id && (
              <div className="border-t border-zinc-800 p-5 space-y-5">
                {/* Features */}
                <div>
                  <h4 className="text-sm font-medium text-zinc-400 mb-2">Features</h4>
                  <div className="flex flex-wrap gap-2">
                    {(plan.features || []).map((f, i) => (
                      <span key={i} className="text-xs bg-zinc-800 text-zinc-300 px-3 py-1 rounded-full">{f}</span>
                    ))}
                    {(!plan.features || plan.features.length === 0) && <span className="text-zinc-600 text-sm">No features listed</span>}
                  </div>
                </div>

                {/* Per-Site Pricing */}
                <div>
                  <h4 className="text-sm font-medium text-zinc-400 mb-2 flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5" /> Per-Site Pricing
                    <span className="text-zinc-600 text-xs">(overrides site-wide price)</span>
                  </h4>

                  {plan.site_prices && plan.site_prices.length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {plan.site_prices.map(sp => (
                        <div key={sp.site_id} className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-4 py-2">
                          <span className="text-sm text-white">{sp.site_name}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-white">£{sp.price_monthly?.toFixed(2)}/mo</span>
                            <button onClick={() => removeSitePrice(plan.id, sp.site_id)} className="text-zinc-500 hover:text-red-400" data-testid={`remove-site-price-${sp.site_id}`}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-600 text-sm mb-3">No per-site overrides. All sites use £{plan.price_monthly?.toFixed(2)}/mo.</p>
                  )}

                  {/* Add site price */}
                  <div className="flex items-center gap-2">
                    <select
                      value={sitePriceForm.site_id}
                      onChange={e => setSitePriceForm(p => ({ ...p, site_id: e.target.value }))}
                      className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
                      data-testid="site-price-site-select"
                    >
                      <option value="">Select site...</option>
                      {sites.filter(s => !(plan.site_prices || []).find(sp => sp.site_id === s.id)).map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <input
                      type="number" step="0.01" placeholder="£ Price"
                      value={sitePriceForm.price_monthly}
                      onChange={e => setSitePriceForm(p => ({ ...p, price_monthly: e.target.value }))}
                      className="w-28 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
                      data-testid="site-price-amount"
                    />
                    <button
                      onClick={() => saveSitePrice(plan.id)}
                      disabled={!sitePriceForm.site_id || !sitePriceForm.price_monthly}
                      className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-40"
                      data-testid="add-site-price-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {plans.length === 0 && !loading && (
          <div className="text-center py-16 text-zinc-500">No plans found. Create your first plan.</div>
        )}
      </div>

      {/* Edit/Create Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => { setEditing(null); setShowCreate(false); }}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="plan-edit-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">{showCreate ? 'Create Plan' : 'Edit Plan'}</h2>
              <button onClick={() => { setEditing(null); setShowCreate(false); }} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <PlanForm plan={editing} onChange={setEditing} onSave={() => savePlan(editing)} />
          </div>
        </div>
      )}
    </div>
  );
};

const PlanForm = ({ plan, onChange, onSave }) => {
  const updateFeature = (idx, val) => {
    const f = [...(plan.features || [])];
    f[idx] = val;
    onChange({ ...plan, features: f });
  };
  const addFeature = () => onChange({ ...plan, features: [...(plan.features || []), ''] });
  const removeFeature = (idx) => onChange({ ...plan, features: (plan.features || []).filter((_, i) => i !== idx) });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-zinc-400 text-xs mb-1 block">Plan Name *</label>
          <input value={plan.name} onChange={e => onChange({ ...plan, name: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="plan-name-input" />
        </div>
        <div>
          <label className="text-zinc-400 text-xs mb-1 block">Code</label>
          <input value={plan.code} onChange={e => onChange({ ...plan, code: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Auto-generated" data-testid="plan-code-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-zinc-400 text-xs mb-1 block">Site-Wide Price (£/month) *</label>
          <input type="number" step="0.01" value={plan.price_monthly} onChange={e => onChange({ ...plan, price_monthly: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="plan-price-input" />
        </div>
        <div>
          <label className="text-zinc-400 text-xs mb-1 block">Plan Type *</label>
          <select value={plan.plan_type} onChange={e => onChange({ ...plan, plan_type: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="plan-type-select">
            {PLAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-zinc-400 text-xs mb-1 block">Description</label>
        <textarea value={plan.description || ''} onChange={e => onChange({ ...plan, description: e.target.value })} rows={2}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="plan-desc-input" />
      </div>
      <div>
        <label className="text-zinc-400 text-xs mb-1 block">Features</label>
        <div className="space-y-2">
          {(plan.features || []).map((f, i) => (
            <div key={i} className="flex gap-2">
              <input value={f} onChange={e => updateFeature(i, e.target.value)}
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Feature..." />
              <button onClick={() => removeFeature(i)} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={addFeature} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add Feature
          </button>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={plan.is_public} onChange={e => onChange({ ...plan, is_public: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 text-red-600" />
          <span className="text-sm text-zinc-300">Public (visible on website)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={plan.access_all_sites} onChange={e => onChange({ ...plan, access_all_sites: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 text-red-600" />
          <span className="text-sm text-zinc-300">Multi-site access</span>
        </label>
      </div>
      <button onClick={onSave} className="w-full py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 flex items-center justify-center gap-2" data-testid="save-plan-btn">
        <Save className="w-4 h-4" /> Save Plan
      </button>
    </div>
  );
};

export default AdminPlans;
